import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuthContext } from "../hooks/useAuthContext";
import { ApiConnectionString } from "../constants/ApiConntection";
import LoadingSpinner from "../components/LoadingSpinner";

export default function ServicesSection() {
  const lastSavedServicesRef = useRef({});
  const serviceAbortRef = useRef({});
  const [services, setServices] = useState([]);

  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const { user } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [addingService, setAddingService] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const fetchData = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const response = await axios.get(`${ApiConnectionString}/api/PractitionerService`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      setServices(response.data);

      lastSavedServicesRef.current = (response.data || []).reduce((acc, s) => {
        if (s.id) {
          acc[s.id] = {
            title: s.title ?? "",
            description: s.description ?? "",
          };
        }
        return acc;
      }, {});
    } catch (err) {
      setSubmitSuccess(false);
      setSubmitError(err?.response?.data || err?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const updateService = (index, key, value) => {
    setServices((prev) => prev.map((s, i) => (i === index ? { ...s, [key]: value } : s)));
  };

  const addService = async () => {
    if (!user) return;

    setAddingService(true);
    try {
      const response = await axios.post(`${ApiConnectionString}/api/PractitionerService`, null, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      if (response.status === 200) {
        const newService = {
          title: "",
          description: "",
          id: response.data.id,
          sortOrder: response.data.sortOrder,
        };

        setServices((prev) => [...prev, newService]);

        if (newService.id) {
          lastSavedServicesRef.current = {
            ...lastSavedServicesRef.current,
            [newService.id]: { title: "", description: "" },
          };
        }
      }
    } catch (err) {
      setSubmitSuccess(false);
      setSubmitError(err?.response?.data || err?.message || "Something went wrong");
    } finally {
      setAddingService(false);
    }
  };

  const removeService = async (index) => {
    if (!user) return;
    if (services.length <= 1) return;

    const target = services[index];
    if (!target) return;

    if (!target.id) {
      setServices((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    setRemovingId(target.id);
    try {
      await axios.delete(`${ApiConnectionString}/api/PractitionerService/${target.id}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      setServices((prev) => prev.filter((_, i) => i !== index));

      const copy = { ...lastSavedServicesRef.current };
      delete copy[target.id];
      lastSavedServicesRef.current = copy;

      setSubmitSuccess(true);
      setSubmitError("");
    } catch (err) {
      setSubmitSuccess(false);
      setSubmitError(err?.response?.data || err?.message || "Something went wrong");
    } finally {
      setRemovingId(null);
    }
  };

  const saveServiceOnBlur = async (serviceId) => {
    if (!user) return;
    if (!serviceId) return;

    const current = services.find((x) => x.id === serviceId);
    if (!current) return;

    const prev = lastSavedServicesRef.current[serviceId] || {};
    const next = {
      title: current.title ?? "",
      description: current.description ?? "",
    };

    const changed = next.title !== (prev.title ?? "") || next.description !== (prev.description ?? "");
    if (!changed) return;

    if (serviceAbortRef.current[serviceId]) {
      serviceAbortRef.current[serviceId].abort();
    }
    serviceAbortRef.current[serviceId] = new AbortController();

    setSavingId(serviceId);
    try {
      await axios.put(
        `${ApiConnectionString}/api/PractitionerService/${serviceId}`,
        { Title: next.title, Description: next.description },
        {
          headers: { Authorization: `Bearer ${user.token}` },
          signal: serviceAbortRef.current[serviceId].signal,
        }
      );

      lastSavedServicesRef.current = {
        ...lastSavedServicesRef.current,
        [serviceId]: next,
      };

      setSubmitSuccess(true);
      setSubmitError("");
    } catch (err) {
      const aborted =
        err?.name === "CanceledError" || err?.name === "AbortError" || err?.code === "ERR_CANCELED";

      if (aborted) return;

      setSubmitSuccess(false);
      setSubmitError(err?.response?.data || err?.message || "Something went wrong");
    } finally {
      setSavingId(null);
    }
  };

  if (isLoading) {
    return (
      <div>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="text-muted">Add the services you want shown</div>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={addService}
          disabled={addingService}
        >
          Add service
        </button>
      </div>

      {submitError ? <div className="alert alert-danger">{submitError}</div> : null}
      {submitSuccess ? <div className="alert alert-success">Saved</div> : null}

      <div className="d-grid gap-3">
        {services.map((s, i) => (
          <div key={s.id || i} className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="row g-3 align-items-start">
                <div className="col-12 col-md-5">
                  <label className="form-label">Service title</label>
                  <input
                    className="form-control"
                    required
                    value={s.title ?? ""}
                    onChange={(e) => updateService(i, "title", e.target.value)}
                    onBlur={() => saveServiceOnBlur(s.id)}
                    placeholder="For example Individual counselling"
                    disabled={removingId === s.id}
                  />
                  <div className="invalid-feedback">
                    Please complete this field or write NA if none applicable
                  </div>
                </div>

                <div className="col-12 col-md-7">
                  <label className="form-label">Service description</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    required
                    value={s.description ?? ""}
                    onChange={(e) => updateService(i, "description", e.target.value)}
                    onBlur={() => saveServiceOnBlur(s.id)}
                    placeholder="A short description of who this is for and what you cover."
                    disabled={removingId === s.id}
                  />
                  <div className="invalid-feedback">
                    Please complete this field or write NA if none applicable
                  </div>
                </div>

                <div className="col-12 d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => removeService(i)}
                    disabled={services.length <= 1 || removingId === s.id}
                  >
                    Remove
                  </button>

                  {savingId === s.id ? (
                    <div className="text-muted small align-self-center">Saving</div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
